import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CartHistoryService } from '@/app/shared/services/cart-history.service';
import { CartService } from '@/app/shared/services/cart.service';
import { CartHistory, UpdateCartHistoryRequest } from '@/app/shared/interface/cart-history.interface';
import { forkJoin } from 'rxjs';

export interface DayGroup {
  dayKey: string;
  dayLabel: string;
  transactions: CartHistory[];
}

@Component({
  selector: 'app-cart-history-modal',
  templateUrl: './cart-history-modal.component.html',
  styleUrls: ['./cart-history-modal.component.scss'],
})
export class CartHistoryModalComponent implements OnDestroy {
  @ViewChild('cartHistoryModal', { static: false }) cartHistoryModal: TemplateRef<any>;
  @Output() restored = new EventEmitter<void>();

  modalOpen = false;
  closeResult = '';

  historyList: CartHistory[] = [];
  isLoading = false;
  isRestoring = false;

  // Pagination & infinite scroll
  currentPage = 1;
  pageSize = 15;
  hasMore = true;
  isLoadingMore = false;

  // Day collapse state
  collapsedDays: Record<string, boolean> = {};

  // Transaction expand state (for showing more/less than 3 items)
  expandedTransactions: Record<string, boolean> = {};
  isDeselectingAll = false;

  // Grouped history cached to prevent recreating arrays and DOM elements on change detection
  groupedHistory: DayGroup[] = [];

  /** historyId → sku → boolean */
  selectedMap: Record<string, Record<string, boolean>> = {};

  constructor(
    private modalService: NgbModal,
    private cartHistoryService: CartHistoryService,
    private cartService: CartService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastrService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  // ── Open / close ──────────────────────────────────────────────────────────

  openModal() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.modalOpen = true;
    this.loadHistory();
    this.modalService
      .open(this.cartHistoryModal, {
        ariaLabelledBy: 'cart-history-modal',
        centered: true,
        size: 'lg',
        windowClass: 'cart-history-modal-window',
        scrollable: true,
      })
      .result.then(
        () => { },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        },
      );
  }

  private getDismissReason(reason: ModalDismissReasons | any): string {
    if (reason === ModalDismissReasons.ESC) return 'by pressing ESC';
    if (reason === ModalDismissReasons.BACKDROP_CLICK) return 'by clicking backdrop';
    return `with: ${reason}`;
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  loadHistory() {
    this.currentPage = 1;
    this.hasMore = true;
    this.isLoading = true;
    this.historyList = [];
    this.selectedMap = {};
    this.collapsedDays = {};
    this.groupedHistory = [];
    this.cdr.detectChanges();

    this.cartHistoryService.getCartHistory(this.pageSize, this.currentPage).subscribe({
      next: (res) => {
        this.historyList = res?.data ?? [];
        const total = res?.total ?? 0;
        this.hasMore = this.historyList.length < total;

        for (const h of this.historyList) {
          this.selectedMap[h.id] = {};
          for (const item of h.items) {
            // Respect isSelected flag returned by API
            this.selectedMap[h.id][item.sku] = item.isSelected ?? false;
          }
        }
        this.groupHistory();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadMoreHistory() {
    if (this.isLoading || this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    this.currentPage++;
    this.cdr.detectChanges();

    this.cartHistoryService.getCartHistory(this.pageSize, this.currentPage).subscribe({
      next: (res) => {
        const newData = res?.data ?? [];
        if (newData.length === 0) {
          this.hasMore = false;
        } else {
          this.historyList = [...this.historyList, ...newData];
          const total = res.total ?? 0;
          this.hasMore = this.historyList.length < total;

          for (const h of newData) {
            if (!this.selectedMap[h.id]) {
              this.selectedMap[h.id] = {};
            }
            for (const item of h.items) {
              this.selectedMap[h.id][item.sku] = item.isSelected ?? false;
            }
          }
          this.groupHistory();
        }
        this.isLoadingMore = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoadingMore = false;
        this.currentPage--;
        this.cdr.detectChanges();
      },
    });
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    // Check if user has scrolled to the bottom (within 50px threshold)
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (atBottom && !this.isLoading && !this.isLoadingMore && this.hasMore) {
      this.loadMoreHistory();
    }
  }

  toggleDay(dayKey: string) {
    this.collapsedDays[dayKey] = !this.collapsedDays[dayKey];
    this.cdr.detectChanges();
  }

  isDayCollapsed(dayKey: string): boolean {
    return !!this.collapsedDays[dayKey];
  }

  groupHistory() {
    const map = new Map<string, DayGroup>();
    for (const h of this.historyList) {
      const key = this.getDayKey(h.createdAt);
      if (!map.has(key)) {
        map.set(key, {
          dayKey: key,
          dayLabel: this.isToday(h.createdAt) ? 'Hôm nay' : this.formatDay(h.createdAt),
          transactions: [],
        });
      }
      map.get(key)!.transactions.push(h);
    }
    this.groupedHistory = Array.from(map.values());
  }

  trackByDay(index: number, day: DayGroup): string {
    return day.dayKey;
  }

  trackByTransaction(index: number, transaction: CartHistory): string {
    return transaction.id;
  }

  trackByItem(index: number, item: any): string {
    return item.sku;
  }

  // ── Selection helpers ─────────────────────────────────────────────────────

  /** Count of all selected items across all transactions */
  get selectedCount(): number {
    let count = 0;
    for (const h of this.historyList) {
      for (const item of h.items) {
        if (this.selectedMap[h.id]?.[item.sku]) count++;
      }
    }
    return count;
  }


  // Item-level
  toggleItem(history: CartHistory, item: any, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;

    if (!this.selectedMap[history.id]) {
      this.selectedMap[history.id] = {};
    }

    this.selectedMap[history.id][item.sku] = checked;
    this.cdr.detectChanges();

    const payload: UpdateCartHistoryRequest = {
      cartHistoryId: history.id,
      items: [
        {
          productId: item.productId,
          quantity: item.quantity,
          isSelected: checked
        }
      ]
    };

    this.cartHistoryService.updateCartHistory(payload).subscribe({
      next: (res: any) => {
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (err && err.error && err.error.message) {
          this.toastService.error(err.error.message);
        } else {
          this.toastService.error('Cập nhật thất bại.');
        }

        // rollback UI nếu lỗi
        this.selectedMap[history.id][item.sku] = !checked;
        this.cdr.detectChanges();
      }
    });
  }

  getTransactionTotalQuantity(history: CartHistory): number {
    if (!history.items) return 0;
    return history.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  isTransactionSelected(history: CartHistory): boolean {
    if (!history.items || history.items.length === 0) return false;
    return history.items.every(item => !!this.selectedMap[history.id]?.[item.sku]);
  }

  isTransactionExpanded(historyId: string): boolean {
    return !!this.expandedTransactions[historyId];
  }

  toggleTransactionExpand(historyId: string) {
    this.expandedTransactions[historyId] = !this.expandedTransactions[historyId];
    this.cdr.detectChanges();
  }

  getVisibleItems(history: CartHistory): any[] {
    if (!history.items) return [];
    if (this.isTransactionExpanded(history.id)) {
      return history.items;
    }
    return history.items.slice(0, 3);
  }

  deselectAll() {
    if (this.isDeselectingAll) return;

    const transactionsToDeselect = this.historyList.filter(h => {
      return h.items.some(item => this.selectedMap[h.id]?.[item.sku]);
    });

    if (transactionsToDeselect.length === 0) return;

    this.isDeselectingAll = true;
    this.cdr.detectChanges();

    const originalSelection = JSON.parse(JSON.stringify(this.selectedMap));

    for (const h of this.historyList) {
      if (this.selectedMap[h.id]) {
        for (const sku in this.selectedMap[h.id]) {
          this.selectedMap[h.id][sku] = false;
        }
      }
    }
    this.cdr.detectChanges();

    const requests = transactionsToDeselect.map(h => {
      const payload: UpdateCartHistoryRequest = {
        cartHistoryId: h.id,
        items: h.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          isSelected: false
        }))
      };
      return this.cartHistoryService.updateCartHistory(payload);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.isDeselectingAll = false;
        this.toastService.success('Đã bỏ chọn tất cả.');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isDeselectingAll = false;
        this.selectedMap = originalSelection;
        if (err && err.error && err.error.message) {
          this.toastService.error(err.error.message);
        } else {
          this.toastService.error('Không thể bỏ chọn tất cả.');
        }
        this.cdr.detectChanges();
      }
    });
  }

  toggleTransaction(history: CartHistory, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;

    if (!this.selectedMap[history.id]) {
      this.selectedMap[history.id] = {};
    }

    // Save original selection state in case we need to rollback
    const originalSelection = { ...this.selectedMap[history.id] };

    // Update all items in local state
    for (const item of history.items) {
      this.selectedMap[history.id][item.sku] = checked;
    }
    this.cdr.detectChanges();

    const payload: UpdateCartHistoryRequest = {
      cartHistoryId: history.id,
      items: history.items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        isSelected: checked
      }))
    };

    this.cartHistoryService.updateCartHistory(payload).subscribe({
      next: (res: any) => {
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (err && err.error && err.error.message) {
          this.toastService.error(err.error.message);
        } else {
          this.toastService.error('Cập nhật thất bại.');
        }

        // rollback UI nếu lỗi
        this.selectedMap[history.id] = originalSelection;
        this.cdr.detectChanges();
      }
    });
  }

  get hasAnySelected(): boolean {
    return this.selectedCount > 0;
  }

  // ── Restore ───────────────────────────────────────────────────────────────

  restoreSelected() {
    this.isRestoring = true;
    this.cdr.detectChanges();
    this.cartService.restoreCartItems().subscribe({
      next: () => {
        this.isRestoring = false;
        this.cdr.detectChanges();
        this.restored.emit();
        this.modalService.dismissAll();
      },
      error: () => {
        this.isRestoring = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ── Date helpers ──────────────────────────────────────────────────────────

  private getDayKey(dateStr: string): string {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  formatDay(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  isToday(dateStr: string): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }

  ngOnDestroy() {
    if (this.modalOpen) {
      this.modalService.dismissAll();
    }
  }
}
